import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";
import {
    LucideIcon,
    ChevronDown,
    ChevronUp,
    ArrowUpDown,
    Printer,
    X,
    Clipboard,
    Trash2,
    FileText,
    Download,
    Upload,
    Plus,
    Minus,
    Search,
    Settings,
    User,
    Home,
    Menu,
    MoreHorizontal,
    MoreVertical,
    Check,
    AlertCircle,
    Info,
    XCircle,
    CheckCircle
} from "lucide-react";

const iconVariants = cva("shrink-0", {
    variants: {
        size: {
            xs: "size-3",
            sm: "size-4",
            md: "size-5",
            lg: "size-6",
            xl: "size-8",
        },
    },
    defaultVariants: {
        size: "md",
    },
});

// Icon mapping for easy access
const iconMap = {
    // Arrows & Navigation
    "chevron-down": ChevronDown,
    "chevron-up": ChevronUp,
    "arrow-up-down": ArrowUpDown,
    dropdown: ChevronDown,
    arrowUp: ChevronUp,
    arrowDown: ChevronDown,
    arrowUpDown: ArrowUpDown,

    // Actions
    print: Printer,
    close: X,
    clipboard: Clipboard,
    trash: Trash2,
    delete: Trash2,
    download: Download,
    upload: Upload,
    plus: Plus,
    minus: Minus,
    search: Search,

    // Interface
    menu: Menu,
    settings: Settings,
    user: User,
    home: Home,
    "more-horizontal": MoreHorizontal,
    "more-vertical": MoreVertical,

    // Status
    check: Check,
    "alert-circle": AlertCircle,
    info: Info,
    "x-circle": XCircle,
    "check-circle": CheckCircle,

    // Documents
    report: FileText,
    file: FileText,
} as const;

export type IconName = keyof typeof iconMap;

export interface IconProps
    extends React.SVGAttributes<SVGElement>,
    VariantProps<typeof iconVariants> {
    name: IconName;
    className?: string;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
    ({ name, size, className, ...props }, ref) => {
        const IconComponent = iconMap[name];

        if (!IconComponent) {
            console.warn(`Icon "${name}" not found`);
            return null;
        }

        return (
            <IconComponent
                ref={ref}
                className={cn(iconVariants({ size }), className)}
                {...props}
            />
        );
    }
);

Icon.displayName = "Icon";

// Export individual icons for direct use
export {
    ChevronDown,
    ChevronUp,
    ArrowUpDown,
    Printer,
    X,
    Clipboard,
    Trash2,
    FileText,
    Download,
    Upload,
    Plus,
    Minus,
    Search,
    Settings,
    User,
    Home,
    Menu,
    MoreHorizontal,
    MoreVertical,
    Check,
    AlertCircle,
    Info,
    XCircle,
    CheckCircle,
};

export type { LucideIcon };
